import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import aspectRatios, { AspectRatioIcon } from '../../constants/aspectRatios';
import resolutionMap, { RESOLUTION_OPTIONS } from '../../constants/resolutionMap';
import { searchPinterestPins, getUserBoards, getBoardPins } from '../../services/pinterestService';
import { generateCombineInteriorStyles, getErrorMessage } from '../../services/geminiService';
import { generateWithFlux, isFluxModel } from '../../services/fluxService';
import { useProjects } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  projectsService,
  savedDesignsService,
  generationMetadataService,
  userProfilesService,
  storageService,
} from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import toast, { Toaster } from 'react-hot-toast';
import UploadedImageThumb from '../../components/UploadedImageThumb';
import AssetLibraryPanel from '../../components/AssetLibraryPanel';
import RoomTypeDropdown from '../../components/RoomTypeDropdown';
import ModelSelectionDropdown from '../../components/ModelSelectionDropdown';
import GenerationStatusNotification from '../../components/GenerationStatusNotification';
import useGenerationTimeout from '../../hooks/useGenerationTimeout';
import { getModelCreditCost } from '../../utils/creditCost';
import { downloadImage } from '../../utils/downloadUtils';
import { checkSaveDesignLimit } from '../../utils/planLimits';

const CombineInteriorStyles = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSaveModal } = useProjects();
  const { user, userProfile, loading, profileLoading } = useAuth();
  const [showGuide, setShowGuide] = useState(false);
  const [guideStep, setGuideStep] = useState(1);
  const [guideAnimating, setGuideAnimating] = useState(false);
  const [guideVisible, setGuideVisible] = useState(false);

  // Guide refs — desktop
  const style1UploadDesktopRef = useRef(null);
  const style2UploadDesktopRef = useRef(null);
  const roomTypeDesktopRef = useRef(null);
  const furniturePresetsDesktopRef = useRef(null);
  const modelDesktopRef = useRef(null);
  const aspectRatioDesktopRef = useRef(null);
  const resolutionGuideDesktopRef = useRef(null);
  const promptDesktopRef = useRef(null);
  const generateBtnDesktopRef = useRef(null);
  const resultDesktopRef = useRef(null);
  const assetLibraryDesktopRef = useRef(null);
  // Guide refs — mobile
  const style1UploadMobileRef = useRef(null);
  const style2UploadMobileRef = useRef(null);
  const roomTypeMobileRef = useRef(null);
  const furniturePresetsMobileRef = useRef(null);
  const modelMobileRef = useRef(null);
  const aspectRatioMobileRef = useRef(null);
  const resolutionGuideMobileRef = useRef(null);
  const promptMobileRef = useRef(null);
  const generateBtnMobileRef = useRef(null);
  const resultMobileRef = useRef(null);
  const assetLibraryMobileRef = useRef(null);

  const guideStepCount = 11;

  const scrollGuideTarget = (step) => {
    const desktopRefs = [style1UploadDesktopRef, style2UploadDesktopRef, roomTypeDesktopRef, furniturePresetsDesktopRef, modelDesktopRef, aspectRatioDesktopRef, resolutionGuideDesktopRef, promptDesktopRef, generateBtnDesktopRef, resultDesktopRef, assetLibraryDesktopRef];
    const mobileRefs = [style1UploadMobileRef, style2UploadMobileRef, roomTypeMobileRef, furniturePresetsMobileRef, modelMobileRef, aspectRatioMobileRef, resolutionGuideMobileRef, promptMobileRef, generateBtnMobileRef, resultMobileRef, assetLibraryMobileRef];
    const ref = window.innerWidth >= 768 ? desktopRefs[step - 1]?.current : mobileRefs[step - 1]?.current;
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const openGuide = () => {
    setGuideStep(1);
    setShowGuide(true);
    // Scroll step 1 target into center of viewport before showing the bubble
    const ref = window.innerWidth >= 768 ? style1UploadDesktopRef.current : style1UploadMobileRef.current;
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setGuideVisible(true));
        });
      }, 500);
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setGuideVisible(true));
      });
    }
  };

  const nextGuideStep = () => {
    setGuideAnimating(true);
    setGuideVisible(false);
    const nextStep = guideStep + 1;
    const needsScroll = nextStep >= 3;
    if (needsScroll) {
      scrollGuideTarget(nextStep);
    }
    setTimeout(() => {
      setGuideStep(nextStep);
      setGuideAnimating(false);
      if (needsScroll) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setGuideVisible(true));
          });
        }, 400);
      } else {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setGuideVisible(true));
        });
      }
    }, 600);
  };

  const closeGuide = () => {
    setGuideVisible(false);
    setTimeout(() => {
      setShowGuide(false);
      setGuideStep(1);
    }, 600);
  };

  const [style1Images, setStyle1Images] = useState([]);
  const [style2Images, setStyle2Images] = useState([]);
  const [selectedModel, setSelectedModel] = useState('Gemini 3.1 Flash Image Preview');
  const [selectedImageSize, setSelectedImageSize] = useState('Auto');
  const [selectedRoomType, setSelectedRoomType] = useState('--');
  const [style1Furniture, setStyle1Furniture] = useState([]);
  const [style2Furniture, setStyle2Furniture] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const promptRef = useRef(null);
  const [isPinterestModalOpen, setIsPinterestModalOpen] = useState(false);
  const [pinterestBoards, setPinterestBoards] = useState([]);
  const [pinterestPins, setPinterestPins] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [pinterestSearchQuery, setPinterestSearchQuery] = useState('');
  const [isPinterestLoading, setIsPinterestLoading] = useState(false);
  const [pinterestError, setPinterestError] = useState('');
  const [pinterestView, setPinterestView] = useState('search'); // 'search', 'boards', 'pins'
  const [currentImage, setCurrentImage] = useState(null);
  const [sessionLibraryImages, setSessionLibraryImages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState('');
  const [isAspectRatioOpen, setIsAspectRatioOpen] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState('1K');
  const [isResolutionOpen, setIsResolutionOpen] = useState(false);
  const [isDragOverStyle1, setIsDragOverStyle1] = useState(false);
  const [isDragOverStyle2, setIsDragOverStyle2] = useState(false);
  const [isDragOverStyle1Upload, setIsDragOverStyle1Upload] = useState(false);
  const [isDragOverStyle2Upload, setIsDragOverStyle2Upload] = useState(false);
  const [libraryAssets, setLibraryAssets] = useState([]);
  const abortControllerRef = useRef(null);
  const isGeneratingRef = useRef(false);
  const aspectRatioRef = useRef(null);
  const resolutionRef = useRef(null);
  const { generationStatus, startTimer, clearTimer, setFailed, setCancelled, dismissNotification } = useGenerationTimeout();

  // Models constant removed - using shared ModelSelectionDropdown component

  const roomTypes = [
  { name: '--', furniture: [] },
  { name: 'Bedroom', furniture: ['Bed', 'Headboard', 'Bedside table', 'Wardrobe', 'Bed frame', 'Bench', 'Rug', 'Curtains', 'Mirror', 'Table lamp'] },
  { name: 'Living room', furniture: ['Sofa', 'Armchair', 'Coffee table', 'Rug', 'TV unit', 'Side table', 'Floor lamp', 'Bookshelf', 'Artwork', 'Cushions'] },
  { name: 'Meeting room', furniture: ['Conference table', 'Meeting chairs', 'Presentation screen', 'Whiteboard', 'Credenza', 'Acoustic panels', 'Ceiling lights', 'Cable box', 'Storage cabinet', 'Wall clock'] },
  { name: 'Bathroom', furniture: ['Vanity unit', 'Sink', 'Mirror', 'Shower enclosure', 'Bathtub', 'Toilet', 'Storage cabinet', 'Towel rack', 'Bath mat', 'Lighting fixture'] },
  { name: 'Cabinet', furniture: ['Desk', 'Task chair', 'Bookshelf', 'Filing cabinet', 'Desk lamp', 'Monitor stand', 'Storage drawers', 'Rug', 'Pinboard', 'Side cabinet'] },
  { name: 'Kitchen', furniture: ['Base cabinets', 'Wall cabinets', 'Countertop', 'Kitchen island', 'Bar stools', 'Sink unit', 'Appliance tower', 'Backsplash panel', 'Pantry cabinet', 'Dining table'] },
  { name: 'Reception', furniture: ['Reception desk', 'Waiting chairs', 'Side table', 'Signage wall', 'Storage cabinet', 'Rug', 'Decorative lighting', 'Brochure stand', 'Coat rack', 'Artwork'] },
  { name: 'Lounge', furniture: ['Lounge sofa', 'Armchairs', 'Low table', 'Ottoman', 'Rug', 'Floor lamp', 'Side tables', 'Cushions', 'Shelving unit', 'Decorative screen'] },
  { name: 'Bar', furniture: ['Bar counter', 'Bar stools', 'Back bar shelving', 'Bottle display', 'High table', 'Pendant lights', 'Storage cabinet', 'Foot rail', 'Glass rack', 'Accent mirror'] },
  { name: 'Terrace', furniture: ['Outdoor sofa', 'Outdoor chairs', 'Outdoor table', 'Sun lounger', 'Planters', 'Outdoor rug', 'Side table', 'Umbrella', 'Bench', 'Lighting fixture'] },
  { name: 'Co-working area', furniture: ['Work desks', 'Task chairs', 'Shared table', 'Phone booth', 'Storage lockers', 'Soft seating', 'Meeting pods', 'Desk lamps', 'Whiteboards', 'Shelving units'] },
  { name: 'Restaurant', furniture: ['Dining tables', 'Dining chairs', 'Banquettes', 'Bar counter', 'Host stand', 'Sideboards', 'Shelving', 'Pendant lights', 'Divider screens', 'Coat rack'] }];


  const getCurrentFurniture = () => {
    const room = roomTypes?.find((r) => r?.name === selectedRoomType);
    return room?.furniture || [];
  };

  // Drag and drop handlers for furniture presets
  const handleDragStart = (e, furniture) => {
    e.dataTransfer.setData('text/plain', furniture);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnterStyle1 = (e) => {
    e.preventDefault();
    setIsDragOverStyle1(true);
  };

  const handleDragLeaveStyle1 = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOverStyle1(false);
    }
  };

  const handleDropStyle1 = (e) => {
    e.preventDefault();
    setIsDragOverStyle1(false);
    const furniture = e.dataTransfer.getData('text/plain');
    if (furniture && !style1Furniture?.includes(furniture)) {
      setStyle1Furniture([...style1Furniture, furniture]);
    }
  };

  const handleDragEnterStyle2 = (e) => {
    e.preventDefault();
    setIsDragOverStyle2(true);
  };

  const handleDragLeaveStyle2 = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOverStyle2(false);
    }
  };

  const handleDropStyle2 = (e) => {
    e.preventDefault();
    setIsDragOverStyle2(false);
    const furniture = e.dataTransfer.getData('text/plain');
    if (furniture && !style2Furniture?.includes(furniture)) {
      setStyle2Furniture([...style2Furniture, furniture]);
    }
  };

  const removeFurniture = (furniture, styleNumber) => {
    if (styleNumber === 1) {
      setStyle1Furniture(style1Furniture?.filter((f) => f !== furniture));
    } else {
      setStyle2Furniture(style2Furniture?.filter((f) => f !== furniture));
    }
  };

  const addCustomFurniture = (styleNumber) => {
    const customItem = prompt(`Enter custom furniture item:`);
    if (customItem?.trim()) {
      if (styleNumber === 1) {
        setStyle1Furniture([...style1Furniture, customItem?.trim()]);
      } else {
        setStyle2Furniture([...style2Furniture, customItem?.trim()]);
      }
    }
  };

  const MAX_TOTAL_UPLOADS = 2;
  const totalUploads = style1Images.length + style2Images.length;

  // Remove image functions
  const removeImage = (index, styleNumber) => {
    if (styleNumber === 1) {
      const newImages = style1Images.filter((_, i) => i !== index);
      setStyle1Images(newImages);
    } else {
      const newImages = style2Images.filter((_, i) => i !== index);
      setStyle2Images(newImages);
    }
  };

  // Image upload drag and drop handlers
  const handleImageDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleStyle1ImageDragEnter = (e) => {
    e.preventDefault();
    setIsDragOverStyle1Upload(true);
  };

  const handleStyle1ImageDragLeave = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOverStyle1Upload(false);
    }
  };

  const handleStyle1ImageDrop = async (e) => {
    e.preventDefault();
    setIsDragOverStyle1Upload(false);
    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
    if (imageUrl && !e.dataTransfer.files.length) {
      const totalCurrent = style1Images.length + style2Images.length;
      if (totalCurrent < MAX_TOTAL_UPLOADS) {
        setStyle1Images((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
      }
      return;
    }
    const remaining = Math.max(0, MAX_TOTAL_UPLOADS - (style1Images.length + style2Images.length));
    if (remaining === 0) return;
    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(file => file.type.startsWith('image/')).slice(0, remaining);
    if (validFiles.length > 0) {
      const imageUrls = (await Promise.all(validFiles.map(file => storageService.uploadFile(file, user?.id)))).filter(Boolean);
      setStyle1Images([...style1Images, ...imageUrls]);
    }
  };

  const handleStyle2ImageDragEnter = (e) => {
    e.preventDefault();
    setIsDragOverStyle2Upload(true);
  };

  const handleStyle2ImageDragLeave = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOverStyle2Upload(false);
    }
  };

  const handleStyle2ImageDrop = async (e) => {
    e.preventDefault();
    setIsDragOverStyle2Upload(false);
    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
    if (imageUrl && !e.dataTransfer.files.length) {
      const totalCurrent = style1Images.length + style2Images.length;
      if (totalCurrent < MAX_TOTAL_UPLOADS) {
        setStyle2Images((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
      }
      return;
    }
    const remaining = Math.max(0, MAX_TOTAL_UPLOADS - (style1Images.length + style2Images.length));
    if (remaining === 0) return;
    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(file => file.type.startsWith('image/')).slice(0, remaining);
    if (validFiles.length > 0) {
      const imageUrls = (await Promise.all(validFiles.map(file => storageService.uploadFile(file, user?.id)))).filter(Boolean);
      setStyle2Images([...style2Images, ...imageUrls]);
    }
  };

  const handleStyle1Upload = async (e) => {
    const remaining = Math.max(0, MAX_TOTAL_UPLOADS - (style1Images.length + style2Images.length));
    if (remaining === 0) return;
    const files = Array.from(e?.target?.files).slice(0, remaining);
    const imageUrls = (await Promise.all(files.map((file) => storageService.uploadFile(file, user?.id)))).filter(Boolean);
    setStyle1Images([...style1Images, ...imageUrls]);
  };

  const handleStyle2Upload = async (e) => {
    const remaining = Math.max(0, MAX_TOTAL_UPLOADS - (style1Images.length + style2Images.length));
    if (remaining === 0) return;
    const files = Array.from(e?.target?.files).slice(0, remaining);
    const imageUrls = (await Promise.all(files.map((file) => storageService.uploadFile(file, user?.id)))).filter(Boolean);
    setStyle2Images([...style2Images, ...imageUrls]);
  };

  // Get image dimensions based on selected size
  const getImageDimensions = () => {
    const ratioMap = resolutionMap[selectedImageSize];
    if (ratioMap && ratioMap[selectedResolution]) {
      return ratioMap[selectedResolution];
    }
    return resolutionMap['1:1'][selectedResolution] || { width: 1024, height: 1024 };
  };

  const getResolutionDimensions = (resKey) => {
    const ratioMap = resolutionMap[selectedImageSize];
    if (ratioMap && ratioMap[resKey]) {
      return ratioMap[resKey];
    }
    return resolutionMap['1:1'][resKey] || { width: 1024, height: 1024 };
  };

  const handleGenerate = async () => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    // Auth check
    const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
    if (!session || !session?.user?.id) {
      toast.error('Please sign in to generate images.');
      isGeneratingRef.current = false;
      return;
    }
    const currentUser = session?.user;

    // Credit check (supports team members sharing owner's pool)
    const creditCheck = await userProfilesService?.checkCredits(currentUser?.id, getModelCreditCost(selectedModel));
    if (!creditCheck?.allowed) {
      toast.error(creditCheck?.message || 'Insufficient credits.');
      isGeneratingRef.current = false;
      return;
    }

    // Check storage limit
    {
      const { allowed, current, max } = await checkSaveDesignLimit(currentUser?.id, userProfile?.subscription_tier);
      if (!allowed) {
        toast.error(`Storage limit reached (${current}/${max}). Please upgrade your plan or delete existing files.`);
        isGeneratingRef.current = false;
        return;
      }
    }

    // Validation with debugging
    console.log('Generate clicked - Debug info:', {
      style1Images: style1Images?.length || 0,
      style2Images: style2Images?.length || 0,
      selectedRoomType,
      style1Furniture: style1Furniture?.length || 0,
      style2Furniture: style2Furniture?.length || 0
    });

    if (!style1Images || style1Images.length === 0) {
      toast.error('Please upload at least one style 1 reference image.');
      isGeneratingRef.current = false;
      return;
    }

    if (!style2Images || style2Images.length === 0) {
      toast.error('Please upload at least one style 2 reference image.');
      isGeneratingRef.current = false;
      return;
    }

    if (!selectedRoomType || selectedRoomType.trim() === '' || selectedRoomType === '--') {
      toast.error('Please select a room type.');
      isGeneratingRef.current = false;
      return;
    }

    setIsGenerating(true);
    startTimer();
    setGenerationError('');
    setCurrentImage(null);

    // Create abort controller
    abortControllerRef.current = new AbortController();

    try {
      const dimensions = getImageDimensions();

      // Build combine-styles-specific system prompt exactly as specified
      const combineStylesSystemPrompt = `Role: You are a neutral translator between interior design platform user inputs and Gemini 3.1 Flash Image Preview image generation model for style-combining visualization.
Send all image references and text inputs uploaded by the user directly to Gemini API in one single call.
Intro: /combine-interior-styles is intended to blend two distinct interior design style references into one cohesive, newly generated room. The user uploads two sets of style reference images — each representing a different interior design mood, aesthetic, or visual direction. The AI extracts the color palette, furniture styles, textiles, objects, materials, and decorative elements from both style references, then generates a completely new room that harmoniously merges elements from both sources. The furniture items assigned to each style via the furniture preset tags define which specific pieces carry which style's visual identity. The generated result must be a hyper-realistic 3D room that feels like a unified, intentional design — not a collage of two clashing styles.
Task: Analyze both style reference image sets. Extract the color palette, material finishes, textures, furniture forms, textile patterns, decorative objects, and atmospheric mood from each. Merge them into a single coherent room composition where elements from style 1 and style 2 coexist naturally. The furniture items tagged under style 1 must adopt the visual identity (materials, colors, forms) of style 1 references. The furniture items tagged under style 2 must adopt the visual identity of style 2 references. The overall room must feel balanced, intentional, and aesthetically unified — as if one designer deliberately chose to blend these two directions. Render with hyper realism and photorealism quality. Preserve the logic and spatial adequacy of generated rooms according to the normal physical human world, with reasonable room dimensions. The room composition must be logically and aesthetically appealing and coherent.
High-quality keywords to maintain in every generation: blended interior design styles, hyper realism, photorealism, 3Ds Max quality with textures and realistic surfaces, shot on professional camera, interior design editorial quality
Temperature: 1 (default Gemini).

You will receive these inputs from the user, and you need to send them to Gemini API in one call, in the following importance hierarchy from one to eight:
Note: first and second styles do not mean importance character, they are equal and must be applied to the final room rendering generation equally
style1Images (REQUIRED) — first style reference images uploaded by the user. These define the first interior design mood: its color palette, material finishes, furniture forms, textile patterns, decorative language, and overall atmosphere. Extract all visual identity from these images precisely. Every element tagged under style 1 furniture must visually follow this reference.
style2Images (REQUIRED) — second style reference images uploaded by the user. These define the second interior design mood. Extract its distinct color palette, material finishes, furniture forms, textile patterns, decorative language, and atmosphere. Every element tagged under style 2 furniture must visually follow this reference.
style1Furniture — array of furniture item names assigned to style 1 by the user (e.g. "Headboard", "Wardrobe"). These specific pieces must be present in the generated room and must carry the visual identity — materials, colors, forms, textures — of style1Images. If empty, apply style 1 mood around 50% (rest 50% is the second mood style) to the final room atmosphere and general furnishing without specific item assignments${style1Furniture.length > 0 ? ': ' + style1Furniture.join(', ') : '.'}
style2Furniture — array of furniture item names assigned to style 2 by the user (e.g. "Table lamp", "Bedside table"). These specific pieces must be present in the generated room and must carry the visual identity of style2Images. If empty, apply style 2 mood around 50% (rest 50% is the first mood style) to the final room atmospheric layer without specific item assignments${style2Furniture.length > 0 ? ': ' + style2Furniture.join(', ') : '.'}
selectedRoomType — include as spatial and functional context. This confirms the room's purpose (bedroom, living room, kitchen, etc.) and determines which furniture presets are suggested to the user. Ensure the generated room respects the functional logic of this room type${selectedRoomType && selectedRoomType !== '--' ? ': ' + selectedRoomType : '.'}
prompt — include the user's text literally, word for word. If the user provides specific blending preferences, dominant style direction, or composition requests, honor them${prompt ? ': ' + prompt : '.'}
selectedImageSize — extract ratio strictly from what the user selected in the UI. If Auto, pass a standard aspect ratio. If a specific size is selected, pass that value exactly. Do not hardcode any dimensions: ${selectedImageSize}.
selectedModel — use only to determine which image generation API to call. Do not pass the model name as text in the prompt.

STRICT RULES:
Do not invent materials, furniture, textiles, objects, or decorative elements not visible in style1Images or style2Images. The two style references are the complete visual vocabulary — nothing is added from outside these sources.
Do not use any style labels unless the user explicitly wrote them in prompt. Let the uploaded visual references define the style naturally through their materials, colors, and forms.
Furniture tagged under style1Furniture must visually belong to style 1. Furniture tagged under style2Furniture must visually belong to style 2. Do not cross-assign or blend individual pieces across the wrong style source.
The two styles must coexist harmoniously in one room — not as two separate halves or zones, but as a unified, intentionally curated interior where both moods feel natural together.
All furniture items listed in style1Furniture and style2Furniture must appear in the generated room. Do not omit any tagged item.
Maintain physical and spatial plausibility at all times. Furniture must respect gravity, wall boundaries, door clearances, and walkable pathways. The room must function as a real human space matching the selectedRoomType.
If any input is missing or '--', skip it silently without generating filler content.
No explanations, no formatting, no headings in output.`;

      const baseInstruction = "Generate exactly what the prompt describes. Do not add style, materials, or aesthetic elements not mentioned in the prompt. Render with hyper realism and photorealism quality.";
      const fullPrompt = `${baseInstruction}\n\n${combineStylesSystemPrompt}`;

      let generatedImage;

      if (isFluxModel(selectedModel)) {
        generatedImage = await generateWithFlux({
          modelName: selectedModel,
          directPromptText: fullPrompt,
          userPromptText: prompt,
          moodboardImages: [...style1Images, ...style2Images],
          roomType: selectedRoomType === 'Custom' ? '' : selectedRoomType,
          imageSize: selectedImageSize,
          signal: abortControllerRef.current.signal,
        });
      } else {
        generatedImage = await generateCombineInteriorStyles({
          style1Images,
          style2Images,
          style1Furniture,
          style2Furniture,
          selectedRoomType,
          prompt,
          selectedImageSize,
          modelName: selectedModel,
          dimensions,
          toolSystemPrompt: fullPrompt,
          signal: abortControllerRef.current.signal,
        });
      }

      // Only deduct credits after successful generation
      setCurrentImage(generatedImage);
      setLibraryAssets((prev) => [...prev, generatedImage]);
      setSessionLibraryImages((prev) => [...prev, generatedImage]);
      toast.success('Combined interior style generated successfully!');

      // Save to database
      await saveGenerationToDatabase(generatedImage, prompt?.trim() || '', currentUser);

      // Deduct credits
      await userProfilesService?.updateCredits(currentUser?.id, getModelCreditCost(selectedModel));

      showSaveModal(generatedImage, location.pathname);
      clearTimer();

    } catch (error) {
      if (error.name === 'AbortError' || error.message.includes('cancelled')) {
        // User cancelled - no charge
        setCancelled();
      } else {
        setFailed();
        setGenerationError(getErrorMessage(error));
      }
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
      isGeneratingRef.current = false;
    }
  };

  // Save generation to database
  const saveGenerationToDatabase = async (imageUrl, promptText, currentUser = null) => {
    const activeUser = currentUser || user;
    if (!activeUser) return;

    try {
      // Upload image to Supabase Storage (convert base64 → public URL)
      const storageUrl = await storageService.uploadImage(imageUrl, activeUser?.id);
      imageUrl = storageUrl;

      let projectId = currentProjectId;
      if (!projectId) {
        const projectName = `${selectedRoomType || 'Combined Styles'} - ${new Date()?.toLocaleDateString()}`;
        const { data: project, error: projectError } = await projectsService?.createProject(activeUser?.id, {
          name: projectName,
          description: promptText,
          thumbnail: imageUrl,
          toolType: 'combine-styles',
          toolName: 'Combine Interior Styles',
          status: 'completed',
          tags: [selectedRoomType, selectedModel]?.filter(Boolean),
          variations: 1,
        });
        if (projectError) {
          console.error('Error creating project:', projectError);
          return;
        }
        projectId = project?.id;
        setCurrentProjectId(projectId);
      } else {
        await projectsService?.incrementVariations(projectId);
        await projectsService?.updateProject(projectId, { thumbnail: imageUrl });
      }

      const { data: design, error: designError } = await savedDesignsService?.createDesign(activeUser?.id, projectId, {
        imageUrl,
        prompt: promptText,
        isFavorite: false,
      });
      if (designError) {
        console.error('Error saving design:', designError);
        return;
      }

      await generationMetadataService?.createMetadata(activeUser?.id, design?.id, {
        model_used: selectedModel,
        image_size: selectedImageSize,
        room_type: selectedRoomType || 'Room',
        style: 'Combined Styles',
        resolution: selectedImageSize,
        lighting: 'Auto',
        quality: 'High',
        usedMedia: [...style1Images, ...style2Images],
        additionalParams: {
          prompt: promptText,
          style1Furniture,
          style2Furniture,
        },
      });
    } catch (error) {
      console.error('Error saving to database:', error);
    }
  };

  // Handle cancel generation
  const handleCancelGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setCancelled();
    setIsGenerating(false);
  };

  // Download image
  const handleDownload = async () => {
    if (!currentImage) return;
    await downloadImage(currentImage, `combined-interior-styles-${Date.now()}.png`);
  };

  // Restore project data when opened from /project-history
  useEffect(() => {
    const projectId = location.state?.projectId;
    if (!projectId) return;
    const loadProjectData = async () => {
      setCurrentProjectId(projectId);
      const designs = await savedDesignsService.getDesigns(projectId);
      if (designs && designs.length > 0) {
        const imageUrls = designs.map((d) => d.imageUrl).filter(Boolean);
        if (imageUrls.length > 0) setSessionLibraryImages(imageUrls);
        setCurrentImage(designs[0].imageUrl);
        // Restore generation settings from metadata
        const metadata = await generationMetadataService.getMetadata(designs[0].id);
        if (metadata) {
          if (metadata.modelUsed) setSelectedModel(metadata.modelUsed);
          if (metadata.imageSize) setSelectedImageSize(metadata.imageSize);
          if (metadata.roomType && metadata.roomType !== 'Room') setSelectedRoomType(metadata.roomType);
          if (metadata?.usedMedia?.length > 0) setLibraryAssets(metadata.usedMedia);
          const restoredPrompt = metadata.additionalParams?.prompt || designs[0].prompt;
          if (restoredPrompt) setPrompt(restoredPrompt);
          if (metadata.additionalParams?.style1Furniture?.length > 0) setStyle1Furniture(metadata.additionalParams.style1Furniture);
          if (metadata.additionalParams?.style2Furniture?.length > 0) setStyle2Furniture(metadata.additionalParams.style2Furniture);
        }
      }
    };
    loadProjectData();
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (promptRef?.current) {
      promptRef.current.style.height = 'auto';
      promptRef.current.style.height = promptRef?.current?.scrollHeight + 'px';
    }
  }, [prompt]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (aspectRatioRef.current && !aspectRatioRef.current.contains(event.target)) {
        setIsAspectRatioOpen(false);
      }
      if (resolutionRef.current && !resolutionRef.current.contains(event.target)) {
        setIsResolutionOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsAspectRatioOpen]);

  // Pinterest Functions
  const handlePinterestSearch = async () => {
    if (!pinterestSearchQuery?.trim()) {
      setPinterestError('Please enter a search query');
      return;
    }

    setIsPinterestLoading(true);
    setPinterestError('');
    try {
      const searchQuery = `${pinterestSearchQuery} ${selectedRoomType || 'interior design'}`;
      const pins = await searchPinterestPins(searchQuery, 50);
      setPinterestPins(pins);
      setPinterestView('pins');
    } catch (error) {
      setPinterestError(error?.message || 'Failed to search Pinterest');
    } finally {
      setIsPinterestLoading(false);
    }
  };

  const handleLoadBoards = async () => {
    setIsPinterestLoading(true);
    setPinterestError('');
    try {
      const boards = await getUserBoards();
      setPinterestBoards(boards);
      setPinterestView('boards');
    } catch (error) {
      setPinterestError(error?.message || 'Failed to load boards');
    } finally {
      setIsPinterestLoading(false);
    }
  };

  const handleBoardSelect = async (board) => {
    setSelectedBoard(board);
    setIsPinterestLoading(true);
    setPinterestError('');
    try {
      const pins = await getBoardPins(board?.id, 50);
      setPinterestPins(pins);
      setPinterestView('pins');
    } catch (error) {
      setPinterestError(error?.message || 'Failed to load board pins');
    } finally {
      setIsPinterestLoading(false);
    }
  };

  const handlePinSelect = (pin) => {
    const remaining = Math.max(0, MAX_TOTAL_UPLOADS - (style1Images.length + style2Images.length));
    if (remaining === 0) {
      toast.error('Maximum of 2 images reached across both styles.');
      return;
    }
    // Add selected pin to style1 or style2 images based on user choice
    const choice = window.confirm(`Add "${pin?.title}" to:\n\nOK = Interior room style 1\nCancel = Interior room style 2`);
    if (choice) {
      setStyle1Images([...style1Images, pin?.imageUrl]);
    } else {
      setStyle2Images([...style2Images, pin?.imageUrl]);
    }
  };

  const resetPinterestModal = () => {
    setPinterestView('search');
    setPinterestSearchQuery('');
    setPinterestPins([]);
    setPinterestBoards([]);
    setSelectedBoard(null);
    setPinterestError('');
  };

  return (
    <>
      <GenerationStatusNotification status={generationStatus} onDismiss={dismissNotification} />
      <Helmet>
        <title>Combine Interior Styles - Roomwork.ai</title>
        <meta name="description" content="Merge distinct design aesthetics to create unique hybrid interior concepts using AI-powered style synthesis" />
      </Helmet>
      <div className="min-h-screen bg-black overflow-x-hidden">
        <Header />
        <main className="pt-20 lg:pt-24 px-4 pb-[20vh]">
          <div className="max-w-[1800px] mx-auto">
            {/* Desktop Header — aligned to content columns */}
            <div className="hidden md:flex gap-4 justify-center mb-6">
              <div className="w-[380px] flex-shrink-0">
                <button
                  onClick={() => navigate('/main-dashboard')}
                  className="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-4">
                  <Icon name="ArrowLeft" size={20} />
                  <span className="text-sm">Back to Dashboard</span>
                </button>
                <h1 className="text-3xl font-bold text-white mb-2">
                  Combine Interior Styles
                </h1>
              </div>
              <div className="flex-1 min-w-0 max-w-[788px] flex items-end justify-end gap-3">
                <button
                  onClick={() => openGuide()}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium hover:scale-[1.02] transition-all flex-shrink-0"
                  style={{ background: 'rgba(255, 255, 255, 0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255, 255, 255, 0.4)', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.25)' }}
                >
                  <Icon name="HelpCircle" size={18} />
                  <span className="hidden lg:inline">Virtual Tool Guide</span>
                </button>
                <button
                  onClick={() => navigate('/project-history')}
                  className="flex items-center gap-2 px-4 py-2 glassmorphic-button rounded-xl text-white text-sm hover:scale-105 transition-transform flex-shrink-0">
                  <Icon name="History" size={18} />
                  <span className="hidden lg:inline text-sm font-medium">View Projects</span>
                </button>
              </div>
            </div>

            {/* Mobile Header */}
            <div className="md:hidden mb-6">
              <button
                onClick={() => navigate('/main-dashboard')}
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-4">
                <Icon name="ArrowLeft" size={20} />
                <span className="text-sm">Back to Dashboard</span>
              </button>
              <div className="flex items-center justify-between gap-2">
                <h1 className="text-3xl font-bold text-white">
                  Combine Interior Styles
                </h1>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openGuide()}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-white text-xs font-medium hover:scale-[1.02] transition-all whitespace-nowrap flex-shrink-0"
                    style={{ background: 'rgba(255, 255, 255, 0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255, 255, 255, 0.4)', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.25)' }}
                  >
                    <Icon name="HelpCircle" size={16} />
                    Guide
                  </button>
                  <button
                    onClick={() => navigate('/project-history')}
                    className="flex items-center gap-2 px-4 py-2 glassmorphic-button rounded-xl text-white text-sm hover:scale-105 transition-transform flex-shrink-0">
                    <Icon name="History" size={18} />
                    <span className="hidden md:inline">View Projects</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop/Tablet/Laptop Layout (hidden on mobile) */}
            <div className="hidden md:flex gap-4 justify-center">
              {/* First Third - Connect Pinterest, Room Type, Model Selection */}
              <div className="w-[380px] flex-shrink-0 space-y-4">
                {/* Connect Pinterest Boards Section */}
                <div className="glassmorphic-card rounded-3xl p-6">
                  <h3 className="text-[12.6px] font-medium text-white mb-4">Connect Pinterest Boards</h3>
                  <button
                    onClick={() => setIsPinterestModalOpen(true)}
                    className="w-full glassmorphic-button rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform">
                    <Icon name="Link" size={18} />
                    Connect Pinterest
                  </button>
                </div>

                {/* Room Type and Furniture Selection */}
                <div ref={roomTypeDesktopRef} className="glassmorphic-card rounded-3xl p-6 space-y-4 overflow-visible relative z-40" style={{ boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <RoomTypeDropdown
                    value={selectedRoomType}
                    onChange={(val) => {
                      setSelectedRoomType(val);
                      setStyle1Furniture([]);
                      setStyle2Furniture([]);
                      if (val === 'Custom') {
                        setTimeout(() => {
                          if (promptRef?.current) {
                            promptRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            promptRef.current.focus();
                          }
                        }, 80);
                      }
                    }}
                    dropDirection="down"
                    showCustomOption
                  />

                  {/* Furniture Presets */}
                  {selectedRoomType &&
                  <div ref={furniturePresetsDesktopRef} style={{ boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1.5rem' }}>
                      <label className="text-white/70 text-[12.6px] mb-2 block">Furniture Presets</label>
                      <div className="flex flex-wrap gap-2">
                        {getCurrentFurniture()?.map((furniture, idx) =>
                      <div
                        key={idx}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, furniture)}
                        className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-xs hover:bg-orange-500/20 hover:border-orange-500/40 transition-all cursor-grab active:cursor-grabbing select-none">
                            {furniture}
                          </div>
                      )}
                      </div>
                    </div>
                  }

                  {/* Interior room style 1 - Furniture Assignment */}
                  <div>
                    <label className="text-white/70 text-[12.6px] mb-2 block">Interior room style 1</label>
                    <div
                      className={`glassmorphic-input rounded-2xl px-4 py-3 min-h-[60px] bg-white/5 border transition-all ${
                        isDragOverStyle1
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-white/10 hover:border-orange-500/30'
                      }`}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnterStyle1}
                      onDragLeave={handleDragLeaveStyle1}
                      onDrop={handleDropStyle1}
                    >
                      <div className="flex flex-wrap gap-2">
                        {style1Furniture?.map((furniture, idx) =>
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-orange-500/20 border border-orange-500/40 text-white text-xs">
                            {furniture}
                            <button
                            onClick={() => removeFurniture(furniture, 1)}
                            className="hover:text-red-400 transition-colors">
                              <Icon name="X" size={12} />
                            </button>
                          </span>
                        )}
                        {style1Furniture.length === 0 && (
                          <div className="flex items-center justify-center gap-2 text-white/50 text-xs w-full py-2">
                            <Icon name="MousePointer2" size={14} />
                            <span>Drag furniture items here</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Interior room style 2 - Furniture Assignment */}
                  <div>
                    <label className="text-white/70 text-[12.6px] mb-2 block">Interior room style 2</label>
                    <div
                      className={`glassmorphic-input rounded-2xl px-4 py-3 min-h-[60px] bg-white/5 border transition-all ${
                        isDragOverStyle2
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-white/10 hover:border-orange-500/30'
                      }`}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnterStyle2}
                      onDragLeave={handleDragLeaveStyle2}
                      onDrop={handleDropStyle2}
                    >
                      <div className="flex flex-wrap gap-2">
                        {style2Furniture?.map((furniture, idx) =>
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-orange-500/20 border border-orange-500/40 text-white text-xs">
                            {furniture}
                            <button
                            onClick={() => removeFurniture(furniture, 2)}
                            className="hover:text-red-400 transition-colors">
                              <Icon name="X" size={12} />
                            </button>
                          </span>
                        )}
                        {style2Furniture.length === 0 && (
                          <div className="flex items-center justify-center gap-2 text-white/50 text-xs w-full py-2">
                            <Icon name="MousePointer2" size={14} />
                            <span>Drag furniture items here</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Model Selection and Controls */}
                <div className="glassmorphic-card rounded-3xl p-6 space-y-4 overflow-visible relative z-30">
                  <div ref={modelDesktopRef} className="relative z-40" style={{ boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1rem' }}>
                    <ModelSelectionDropdown
                      value={selectedModel}
                      onChange={setSelectedModel}
                      dropDirection="down"
                    />
                  </div>

                  <div ref={(el) => { aspectRatioRef.current = el; aspectRatioDesktopRef.current = el; }} className="relative z-20" style={{ boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1rem' }}>
                    <label className="text-white/70 text-[12.6px] mb-2 block">
                      Aspect Ratio
                    </label>
                    <div className="relative">
                      {/* Dropdown trigger */}
                      <button
                        onClick={() =>
                          setIsAspectRatioOpen(!isAspectRatioOpen)
                        }
                        className="w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <AspectRatioIcon
                            ratio={selectedImageSize}
                            size={18}
                            className="text-orange-500"
                          />
                          <span className="text-white text-sm">
                            {selectedImageSize}
                          </span>
                        </div>
                        <Icon
                          name={
                            isAspectRatioOpen ? "ChevronUp" : "ChevronDown"
                          }
                          size={16}
                          className="text-white/50"
                        />
                      </button>
                      {/* Dropdown menu */}
                      {isAspectRatioOpen && (
                        <div
                          className="absolute z-50 w-full top-full mt-2 rounded-2xl border border-white/20 shadow-2xl bg-black overflow-hidden max-h-[300px] overflow-y-auto"
                          style={{
                            backgroundColor: "rgb(0, 0, 0)",
                            backdropFilter: "none"
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {aspectRatios?.map((ratio) => (
                            <button
                              key={ratio?.value}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedImageSize(ratio?.value);
                                setIsAspectRatioOpen(false);
                              }}
                              className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 cursor-pointer ${
                                selectedImageSize === ratio?.value
                                  ? "bg-orange-500/20 hover:bg-orange-500/30"
                                  : "hover:bg-white/5"
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <AspectRatioIcon
                                  ratio={ratio?.value}
                                  size={18}
                                  className={
                                    selectedImageSize === ratio?.value
                                      ? "text-orange-500"
                                      : "text-white/50"
                                  }
                                />
                                <span
                                  className={`text-sm ${
                                    selectedImageSize === ratio?.value
                                      ? "text-white font-medium"
                                      : "text-white/70"
                                  }`}
                                >
                                  {ratio?.label}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div ref={(el) => { resolutionRef.current = el; resolutionGuideDesktopRef.current = el; }} style={{ boxShadow: showGuide && guideStep === 7 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1rem' }}>
                    {!isFluxModel(selectedModel) && (
                    <>
                    <label className="text-white/70 text-[12.6px] mb-2 block">Resolution</label>
                    <div className="relative">
                      <button
                        onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                        className={`w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all`}>
                        <span className="text-white text-sm font-medium">{selectedResolution}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-white/50 text-xs">
                              {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                            </span>
                          <Icon name={isResolutionOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                        </div>
                      </button>
                      {isResolutionOpen && (
                        <div
                          className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/20 shadow-2xl bg-black overflow-hidden"
                          style={{
                            backgroundColor: "rgb(0, 0, 0)",
                            backdropFilter: "none"
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {RESOLUTION_OPTIONS.map((resKey) => {
                            const dims = getResolutionDimensions(resKey);
                            return (
                              <button
                                key={resKey}
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedResolution(resKey);
                                  setIsResolutionOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 cursor-pointer ${
                                  selectedResolution === resKey
                                    ? 'bg-orange-500/20 hover:bg-orange-500/30'
                                    : 'hover:bg-white/5'
                                }`}
                              >
                                <span className={`text-sm ${selectedResolution === resKey ? 'text-white font-medium' : 'text-white/70'}`}>
                                  {resKey}
                                </span>
                                <span className={`text-xs ${selectedResolution === resKey ? 'text-orange-400' : 'text-white/50'}`}>
                                    {dims.width}×{dims.height}
                                  </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    </>
                    )}
                  </div>

                  <div ref={promptDesktopRef} style={{ boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1rem' }}>
                    <label className="text-white/70 text-[12.6px] mb-2 block">Prompt:</label>
                    {selectedRoomType === 'Custom' && (
                      <p className="text-orange-400/80 text-[11px] mb-2 flex items-center gap-1">✏️ Describe the room type you want in the prompt below</p>
                    )}
                    <textarea
                      ref={promptRef}
                      value={prompt}
                      onChange={(e) => setPrompt(e?.target?.value)}
                      placeholder={selectedRoomType === 'Custom' ? 'Describe the room type (e.g. yoga studio, reading nook, wine cellar...)' : 'Additional request'}
                      rows={5}
                      className={`w-full glassmorphic-input rounded-2xl px-4 py-3 text-white bg-white/5 focus:outline-none transition-all placeholder:text-white/30 resize-none overflow-y-auto min-h-[120px] max-h-[300px] ${selectedRoomType === 'Custom' ? 'border-2 border-orange-500/60 focus:border-orange-500' : 'border border-white/10 focus:border-orange-500/50'}`}
                      style={{ height: '120px' }}
                    />
                  </div>

                  <div ref={generateBtnDesktopRef} className="rounded-2xl" style={{ boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating || !style1Images?.length || !style2Images?.length || !selectedRoomType}
                    className="w-full glassmorphic-button rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100">
                    {isGenerating ? (
                      <>
                        <Icon name="Loader" size={18} className="animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Icon name="Sparkles" size={18} />
                        Generate Image
                      </>
                    )}
                  </button>
                  </div>
                  {isGenerating && (
                    <button
                      onClick={handleCancelGeneration}
                      className="w-full glassmorphic-button rounded-2xl py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors"
                    >
                      <Icon name="X" size={16} />
                      Cancel
                    </button>
                  )}

                  <p className="text-white/50 text-xs text-center">Your request will cost {getModelCreditCost(selectedModel)} credit{getModelCreditCost(selectedModel) !== 1 ? 's' : ''}</p>
                </div>
              </div>

              {/* Second and Third Thirds Combined - Result Window + Upload Sections */}
              <div className="flex-1 min-w-0 max-w-[788px] space-y-4">
                {/* Single Large Result Window - MOVED TO TOP */}
                <div ref={resultDesktopRef} className="glassmorphic-card rounded-3xl p-4 relative" style={{ boxShadow: showGuide && guideStep === 10 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <div className="flex justify-between items-center mb-3 px-2">
                    <span className="text-white/70 text-[12.6px]"></span>
                    <span className="text-white/70 text-[12.6px]">Result</span>
                  </div>
                  <div className="relative rounded-2xl overflow-hidden bg-black/40 h-[500px]">
                    {isGenerating ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <Icon
                            name="Loader"
                            size={48}
                            className="text-orange-500 mx-auto mb-3 animate-spin"
                          />
                          <p className="text-white text-sm font-medium">
                            Combining interior styles...
                          </p>
                          <p className="text-white/60 text-xs mt-1">
                            This may take a moment
                          </p>
                        </div>
                      </div>
                    ) : currentImage ? (
                      <img
                        src={currentImage}
                        alt="Generated combined interior design"
                        className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <Icon
                            name="Image"
                            size={48}
                            className="text-white/30 mx-auto mb-3"
                          />
                          <p className="text-white/50 text-sm">
                            No image generated yet
                          </p>
                          <p className="text-white/30 text-xs mt-1">
                            Upload style references and click Generate
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Action buttons — always visible */}
                    <div className="absolute bottom-4 right-4 flex gap-2">
                      <button
                        onClick={handleDownload}
                        disabled={!currentImage || isGenerating}
                        className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Download">
                        <Icon name="Download" size={18} className="text-white" />
                      </button>
                      <button
                        onClick={() => setCurrentImage(null)}
                        disabled={!currentImage || isGenerating}
                        className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete">
                        <Icon name="Trash2" size={18} className="text-white" />
                      </button>
                      <button
                        onClick={handleGenerate}
                        disabled={!currentImage || isGenerating}
                        className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Regenerate">
                        <Icon name="RefreshCw" size={18} className="text-white" />
                      </button>
                    </div>

                    {/* Error message overlay */}
                    {generationError && !isGenerating && (
                      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                        <div className="text-center max-w-md px-4">
                          <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-3" />
                          <p className="text-red-400 text-sm font-medium mb-2">Generation Failed</p>
                          <p className="text-white/70 text-xs">{generationError}</p>
                          <button
                            onClick={() => setGenerationError('')}
                            className="mt-3 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded-lg text-red-400 text-sm transition-colors">
                            Dismiss
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Upload Sections Side by Side - NOW BELOW RESULT */}
                <div className="text-left mb-2">
                  <span className={`text-sm font-medium ${totalUploads >= MAX_TOTAL_UPLOADS ? "text-red-400" : "text-white/70"}`}>
                    {totalUploads} / {MAX_TOTAL_UPLOADS} images uploaded
                  </span>
                </div>
                <div className="flex gap-4">
                  {/* Interior room style 1 Upload */}
                  <div
                    ref={style1UploadDesktopRef}
                    className="flex-1 glassmorphic-card rounded-3xl p-6"
                    style={{ position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                  >
                    <h3 className="text-[12.6px] font-medium text-white mb-4">Interior room style 1<span className="text-red-500">*</span></h3>
                    <label className={`block ${totalUploads >= MAX_TOTAL_UPLOADS ? 'cursor-not-allowed opacity-50 pointer-events-none' : 'cursor-pointer'}`}>
                      <input
                        type="file"
                        multiple
                        accept="image/png,image/jpeg"
                        onChange={handleStyle1Upload}
                        disabled={totalUploads >= MAX_TOTAL_UPLOADS}
                        className="hidden" />
                      <div
                        className={`glassmorphic-upload-zone rounded-2xl p-8 text-center border-2 border-dashed transition-all duration-300 ${
                          totalUploads >= MAX_TOTAL_UPLOADS
                            ? 'border-white/10 bg-white/5'
                            : isDragOverStyle1Upload
                              ? 'border-orange-500 bg-orange-500/10'
                              : 'border-white/20 hover:border-orange-500/40'
                        }`}
                        onDragOver={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleImageDragOver}
                        onDragEnter={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleStyle1ImageDragEnter}
                        onDragLeave={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleStyle1ImageDragLeave}
                        onDrop={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleStyle1ImageDrop}
                      >
                        <Icon
                          name={isDragOverStyle1Upload && totalUploads < MAX_TOTAL_UPLOADS ? "Upload" : "Image"}
                          size={40}
                          className={`mx-auto mb-3 ${isDragOverStyle1Upload && totalUploads < MAX_TOTAL_UPLOADS ? "text-orange-400" : "text-orange-500"}`}
                        />
                        <p className={`text-sm ${
                          totalUploads >= MAX_TOTAL_UPLOADS ? "text-white/40" : isDragOverStyle1Upload ? "text-white font-medium" : "text-white/70"
                        }`}>
                          {totalUploads >= MAX_TOTAL_UPLOADS ? "Upload limit reached" : isDragOverStyle1Upload ? "Drop images here" : "Upload image PNG or JPG"}
                        </p>
                      </div>
                    </label>
                    {style1Images?.length > 0 &&
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {style1Images?.map((img, idx) =>
                        <UploadedImageThumb key={idx} src={img} alt={`Style 1 ${idx + 1}`} onRemove={() => removeImage(idx, 1)} imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                      )}
                      </div>
                    }
                  </div>

                  {/* Interior room style 2 Upload */}
                  <div ref={style2UploadDesktopRef} className="flex-1 glassmorphic-card rounded-3xl p-6" style={{ position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <h3 className="text-[12.6px] font-medium text-white mb-4">Interior room style 2<span className="text-red-500">*</span></h3>
                    <label className={`block ${totalUploads >= MAX_TOTAL_UPLOADS ? 'cursor-not-allowed opacity-50 pointer-events-none' : 'cursor-pointer'}`}>
                      <input
                        type="file"
                        multiple
                        accept="image/png,image/jpeg"
                        onChange={handleStyle2Upload}
                        disabled={totalUploads >= MAX_TOTAL_UPLOADS}
                        className="hidden" />
                      <div
                        className={`glassmorphic-upload-zone rounded-2xl p-8 text-center border-2 border-dashed transition-all duration-300 ${
                          totalUploads >= MAX_TOTAL_UPLOADS
                            ? 'border-white/10 bg-white/5'
                            : isDragOverStyle2Upload
                              ? 'border-orange-500 bg-orange-500/10'
                              : 'border-white/20 hover:border-orange-500/40'
                        }`}
                        onDragOver={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleImageDragOver}
                        onDragEnter={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleStyle2ImageDragEnter}
                        onDragLeave={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleStyle2ImageDragLeave}
                        onDrop={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleStyle2ImageDrop}
                      >
                        <Icon
                          name={isDragOverStyle2Upload && totalUploads < MAX_TOTAL_UPLOADS ? "Upload" : "Image"}
                          size={40}
                          className={`mx-auto mb-3 ${isDragOverStyle2Upload && totalUploads < MAX_TOTAL_UPLOADS ? "text-orange-400" : "text-orange-500"}`}
                        />
                        <p className={`text-sm ${
                          totalUploads >= MAX_TOTAL_UPLOADS ? "text-white/40" : isDragOverStyle2Upload ? "text-white font-medium" : "text-white/70"
                        }`}>
                          {totalUploads >= MAX_TOTAL_UPLOADS ? "Upload limit reached" : isDragOverStyle2Upload ? "Drop images here" : "Upload image PNG or JPG"}
                        </p>
                      </div>
                    </label>
                    {style2Images?.length > 0 &&
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {style2Images?.map((img, idx) =>
                        <UploadedImageThumb key={idx} src={img} alt={`Style 2 ${idx + 1}`} onRemove={() => removeImage(idx, 2)} imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                      )}
                      </div>
                    }
                  </div>
                </div>
                <div ref={assetLibraryDesktopRef} style={{ boxShadow: showGuide && guideStep === 11 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1.5rem' }}>
                  <AssetLibraryPanel
                    libraryAssets={libraryAssets}
                    setLibraryAssets={setLibraryAssets}
                    generatedImages={sessionLibraryImages}
                    setGeneratedImages={setSessionLibraryImages}
                    currentProjectId={currentProjectId}
                    onImageDeleted={(remaining) => {
                      if (!isGenerating) setCurrentImage(remaining[0] || null);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Mobile Layout (visible only on mobile) */}
            <div className="md:hidden space-y-4">
              {/* Connect Pinterest Boards Section - Mobile */}
              <div className="glassmorphic-card rounded-2xl p-4">
                <h3 className="text-white text-sm font-medium mb-3">Connect Pinterest Boards</h3>
                <button
                  onClick={() => setIsPinterestModalOpen(true)}
                  className="w-full glassmorphic-button rounded-xl py-2.5 text-white text-sm font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform">
                  <Icon name="Link" size={16} />
                  Connect Pinterest
                </button>
              </div>

              {/* Result Window - Mobile - MOVED TO TOP */}
              <div ref={resultMobileRef} className="glassmorphic-card rounded-2xl p-3 relative" style={{ boxShadow: showGuide && guideStep === 10 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <div className="flex justify-between items-center mb-2 px-1">
                  <span className="text-white/70 text-xs"></span>
                  <span className="text-white/70 text-xs">Result</span>
                </div>
                <div className="relative rounded-xl overflow-hidden bg-black/40 h-[280px]">
                  {isGenerating ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon
                          name="Loader"
                          size={32}
                          className="text-orange-500 mx-auto mb-2 animate-spin"
                        />
                        <p className="text-white text-xs font-medium">
                          Combining styles...
                        </p>
                      </div>
                    </div>
                  ) : currentImage ? (
                    <img
                      src={currentImage}
                      alt="Generated combined interior design"
                      className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon
                          name="Image"
                          size={32}
                          className="text-white/30 mx-auto mb-2"
                        />
                        <p className="text-white/50 text-xs">
                          No image generated yet
                        </p>
                        <p className="text-white/30 text-xs mt-1">
                          Upload styles and generate
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Action buttons — always visible */}
                  <div className="absolute bottom-3 right-3 flex gap-1.5">
                    <button
                      onClick={handleDownload}
                      disabled={!currentImage || isGenerating}
                      className="p-2 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download">
                      <Icon name="Download" size={14} className="text-white" />
                    </button>
                    <button
                      onClick={() => setCurrentImage(null)}
                      disabled={!currentImage || isGenerating}
                      className="p-2 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete">
                      <Icon name="Trash2" size={14} className="text-white" />
                    </button>
                    <button
                      onClick={handleGenerate}
                      disabled={!currentImage || isGenerating}
                      className="p-2 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Regenerate">
                      <Icon name="RefreshCw" size={14} className="text-white" />
                    </button>
                  </div>

                  {/* Error overlay - Mobile */}
                  {generationError && !isGenerating && (
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                      <div className="text-center max-w-xs px-2">
                        <Icon name="AlertCircle" size={32} className="text-red-500 mx-auto mb-2" />
                        <p className="text-red-400 text-xs font-medium mb-1">Generation Failed</p>
                        <p className="text-white/70 text-xs">{generationError}</p>
                        <button
                          onClick={() => setGenerationError('')}
                          className="mt-2 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded-lg text-red-400 text-xs transition-colors">
                          Dismiss
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-left mb-2">
                <span className={`text-sm font-medium ${totalUploads >= MAX_TOTAL_UPLOADS ? "text-red-400" : "text-white/70"}`}>
                  {totalUploads} / {MAX_TOTAL_UPLOADS} images uploaded
                </span>
              </div>
              <div
                ref={style1UploadMobileRef}
                className="glassmorphic-card rounded-2xl p-4"
                style={{ position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
              >
                <h3 className="text-white text-sm font-medium mb-3">Interior room style 1<span className="text-red-500">*</span></h3>
                <label className={`block ${totalUploads >= MAX_TOTAL_UPLOADS ? 'cursor-not-allowed opacity-50 pointer-events-none' : 'cursor-pointer'}`}>
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg"
                    onChange={handleStyle1Upload}
                    disabled={totalUploads >= MAX_TOTAL_UPLOADS}
                    className="hidden" />
                  <div
                    className={`glassmorphic-upload-zone rounded-xl p-6 text-center border-2 border-dashed transition-all duration-300 ${
                      totalUploads >= MAX_TOTAL_UPLOADS
                        ? 'border-white/10 bg-white/5'
                        : isDragOverStyle1Upload
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-white/20 hover:border-orange-500/40'
                    }`}
                    onDragOver={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleImageDragOver}
                    onDragEnter={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleStyle1ImageDragEnter}
                    onDragLeave={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleStyle1ImageDragLeave}
                    onDrop={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleStyle1ImageDrop}
                  >
                    <Icon
                      name={isDragOverStyle1Upload && totalUploads < MAX_TOTAL_UPLOADS ? "Upload" : "Image"}
                      size={32}
                      className={`mx-auto mb-2 ${isDragOverStyle1Upload && totalUploads < MAX_TOTAL_UPLOADS ? "text-orange-400" : "text-orange-500"}`}
                    />
                    <p className={`text-xs ${
                      totalUploads >= MAX_TOTAL_UPLOADS ? "text-white/40" : isDragOverStyle1Upload ? "text-white font-medium" : "text-white/70"
                    }`}>
                      {totalUploads >= MAX_TOTAL_UPLOADS ? "Upload limit reached" : isDragOverStyle1Upload ? "Drop images here" : "Upload image PNG or JPG"}
                    </p>
                  </div>
                </label>
                {style1Images?.length > 0 &&
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {style1Images?.map((img, idx) =>
                    <UploadedImageThumb key={idx} src={img} alt={`Style 1 ${idx + 1}`} onRemove={() => removeImage(idx, 1)} imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                  )}
                  </div>
                }
              </div>

              <div ref={style2UploadMobileRef} className="glassmorphic-card rounded-2xl p-4" style={{ position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <h3 className="text-white text-sm font-medium mb-3">Interior room style 2<span className="text-red-500">*</span></h3>
                <label className={`block ${totalUploads >= MAX_TOTAL_UPLOADS ? 'cursor-not-allowed opacity-50 pointer-events-none' : 'cursor-pointer'}`}>
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg"
                    onChange={handleStyle2Upload}
                    disabled={totalUploads >= MAX_TOTAL_UPLOADS}
                    className="hidden" />
                  <div
                    className={`glassmorphic-upload-zone rounded-xl p-6 text-center border-2 border-dashed transition-all duration-300 ${
                      totalUploads >= MAX_TOTAL_UPLOADS
                        ? 'border-white/10 bg-white/5'
                        : isDragOverStyle2Upload
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-white/20 hover:border-orange-500/40'
                    }`}
                    onDragOver={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleImageDragOver}
                    onDragEnter={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleStyle2ImageDragEnter}
                    onDragLeave={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleStyle2ImageDragLeave}
                    onDrop={totalUploads >= MAX_TOTAL_UPLOADS ? undefined : handleStyle2ImageDrop}
                  >
                    <Icon
                      name={isDragOverStyle2Upload && totalUploads < MAX_TOTAL_UPLOADS ? "Upload" : "Image"}
                      size={32}
                      className={`mx-auto mb-2 ${isDragOverStyle2Upload && totalUploads < MAX_TOTAL_UPLOADS ? "text-orange-400" : "text-orange-500"}`}
                    />
                    <p className={`text-xs ${
                      totalUploads >= MAX_TOTAL_UPLOADS ? "text-white/40" : isDragOverStyle2Upload ? "text-white font-medium" : "text-white/70"
                    }`}>
                      {totalUploads >= MAX_TOTAL_UPLOADS ? "Upload limit reached" : isDragOverStyle2Upload ? "Drop images here" : "Upload image PNG or JPG"}
                    </p>
                  </div>
                </label>
                {style2Images?.length > 0 &&
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {style2Images?.map((img, idx) =>
                    <UploadedImageThumb key={idx} src={img} alt={`Style 2 ${idx + 1}`} onRemove={() => removeImage(idx, 2)} imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                  )}
                  </div>
                }
              </div>

              <div ref={assetLibraryMobileRef} style={{ boxShadow: showGuide && guideStep === 11 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1.5rem' }}>
                <AssetLibraryPanel
                  libraryAssets={libraryAssets}
                  setLibraryAssets={setLibraryAssets}
                  generatedImages={sessionLibraryImages}
                  setGeneratedImages={setSessionLibraryImages}
                  currentProjectId={currentProjectId}
                  onImageDeleted={(remaining) => {
                    if (!isGenerating) setCurrentImage(remaining[0] || null);
                  }}
                />
              </div>

              {/* Room Type and Furniture Selection - Mobile */}
              <div ref={roomTypeMobileRef} className="glassmorphic-card rounded-2xl p-4 space-y-3 overflow-visible relative z-40" style={{ boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <RoomTypeDropdown
                  value={selectedRoomType}
                  onChange={(val) => {
                    setSelectedRoomType(val);
                    setStyle1Furniture([]);
                    setStyle2Furniture([]);
                    if (val === 'Custom') {
                      setTimeout(() => {
                        if (promptRef?.current) {
                          promptRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          promptRef.current.focus();
                        }
                      }, 80);
                    }
                  }}
                  mobile
                  dropDirection="down"
                  showCustomOption
                />

                {/* Furniture Presets - Mobile */}
                {selectedRoomType &&
                <div ref={furniturePresetsMobileRef} style={{ boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1.5rem' }}>
                    <label className="text-white/70 text-xs mb-2 block">Furniture Presets</label>
                    <div className="flex flex-wrap gap-1.5">
                      {getCurrentFurniture()?.map((furniture, idx) =>
                    <div
                      key={idx}
                      draggable={true}
                      onDragStart={(e) => handleDragStart(e, furniture)}
                      className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white text-xs hover:bg-orange-500/20 hover:border-orange-500/40 transition-all cursor-grab active:cursor-grabbing select-none">
                          {furniture}
                        </div>
                    )}
                    </div>
                  </div>
                }

                {/* Interior room style 1 - Mobile */}
                <div>
                  <label className="text-white/70 text-xs mb-2 block">Interior room style 1</label>
                  <div
                    className={`glassmorphic-input rounded-xl px-3 py-2.5 min-h-[50px] bg-white/5 border transition-all ${
                      isDragOverStyle1
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-white/10 hover:border-orange-500/30'
                    }`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnterStyle1}
                    onDragLeave={handleDragLeaveStyle1}
                    onDrop={handleDropStyle1}
                  >
                    <div className="flex flex-wrap gap-1.5">
                      {style1Furniture?.map((furniture, idx) =>
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-500/20 border border-orange-500/40 text-white text-xs">
                          {furniture}
                          <button
                          onClick={() => removeFurniture(furniture, 1)}
                          className="hover:text-red-400 transition-colors">
                            <Icon name="X" size={10} />
                          </button>
                        </span>
                      )}
                      {style1Furniture.length === 0 && (
                        <div className="flex items-center justify-center gap-1.5 text-white/50 text-xs w-full py-2">
                          <Icon name="MousePointer2" size={12} />
                          <span>Drag furniture here</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Interior room style 2 - Mobile */}
                <div>
                  <label className="text-white/70 text-xs mb-2 block">Interior room style 2</label>
                  <div
                    className={`glassmorphic-input rounded-xl px-3 py-2.5 min-h-[50px] bg-white/5 border transition-all ${
                      isDragOverStyle2
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-white/10 hover:border-orange-500/30'
                    }`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnterStyle2}
                    onDragLeave={handleDragLeaveStyle2}
                    onDrop={handleDropStyle2}
                  >
                    <div className="flex flex-wrap gap-1.5">
                      {style2Furniture?.map((furniture, idx) =>
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-500/20 border border-orange-500/40 text-white text-xs">
                          {furniture}
                          <button
                          onClick={() => removeFurniture(furniture, 2)}
                          className="hover:text-red-400 transition-colors">
                            <Icon name="X" size={10} />
                          </button>
                        </span>
                      )}
                      {style2Furniture.length === 0 && (
                        <div className="flex items-center justify-center gap-1.5 text-white/50 text-xs w-full py-2">
                          <Icon name="MousePointer2" size={12} />
                          <span>Drag furniture here</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Model Selection - Mobile */}
              <div className="glassmorphic-card rounded-2xl p-4 space-y-3 overflow-visible relative z-30">
                <div ref={modelMobileRef} className="relative z-40" style={{ boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1rem' }}>
                  <ModelSelectionDropdown
                    value={selectedModel}
                    onChange={setSelectedModel}
                    mobile
                    dropDirection="down"
                  />
                </div>

                <div ref={(el) => { aspectRatioMobileRef.current = el; }} className="relative z-50" style={{ boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '0.75rem' }}>
                  <label className="text-white/70 text-xs mb-2 block">Aspect Ratio</label>
                  <button
                    onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
                    className="w-full glassmorphic-select rounded-xl px-3 py-2.5 bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all"
                  >
                    <div className="flex items-center gap-2">
                      <AspectRatioIcon ratio={selectedImageSize} size={16} className="text-orange-500" />
                      <span className="text-white text-sm">{selectedImageSize}</span>
                    </div>
                    <Icon name={isAspectRatioOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-white/50" />
                  </button>
                  {isAspectRatioOpen && (
                    <div
                      className="absolute z-[100] w-full top-full mt-2 rounded-xl border border-white/20 shadow-2xl bg-black overflow-hidden max-h-[250px] overflow-y-auto"
                      style={{ backgroundColor: 'rgb(0, 0, 0)', backdropFilter: 'none' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {aspectRatios?.map((ratio) => (
                        <button
                          key={ratio?.value}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedImageSize(ratio?.value);
                            setIsAspectRatioOpen(false);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2.5 transition-all border-b border-white/5 last:border-b-0 cursor-pointer ${
                            selectedImageSize === ratio?.value
                              ? 'bg-orange-500/20 hover:bg-orange-500/30'
                              : 'hover:bg-white/5'
                          }`}
                        >
                          <AspectRatioIcon
                            ratio={ratio?.value}
                            size={16}
                            className={selectedImageSize === ratio?.value ? 'text-orange-500' : 'text-white/50'}
                          />
                          <span className={`text-sm ${selectedImageSize === ratio?.value ? 'text-white font-medium' : 'text-white/70'}`}>
                            {ratio?.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div ref={promptMobileRef} style={{ boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '0.75rem' }}>
                  <label className="text-white/70 text-xs mb-2 block">Prompt:</label>
                  {selectedRoomType === 'Custom' && (
                    <p className="text-orange-400/80 text-[11px] mb-2 flex items-center gap-1">✏️ Describe the room type you want in the prompt below</p>
                  )}
                  <textarea
                    ref={promptRef}
                    value={prompt}
                    onChange={(e) => setPrompt(e?.target?.value)}
                    placeholder={selectedRoomType === 'Custom' ? 'Describe the room type (e.g. yoga studio, reading nook, wine cellar...)' : 'Additional request'}
                    rows={5}
                    className={`w-full glassmorphic-input rounded-xl px-4 py-3 text-sm text-white bg-white/5 focus:outline-none transition-all placeholder:text-white/30 resize-none overflow-y-auto min-h-[120px] max-h-[300px] ${selectedRoomType === 'Custom' ? 'border-2 border-orange-500/60 focus:border-orange-500' : 'border border-white/10 focus:border-orange-500/50'}`}
                    style={{ height: '120px' }}
                  />
                </div>

                <div ref={generateBtnMobileRef} className="rounded-xl" style={{ boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || !style1Images?.length || !style2Images?.length || !selectedRoomType}
                  className="w-full glassmorphic-button rounded-xl py-2.5 text-white text-sm font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100">
                  {isGenerating ? (
                    <>
                      <Icon name="Loader" size={16} className="animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Icon name="Sparkles" size={16} />
                      Generate Image
                    </>
                  )}
                </button>
                </div>
                {isGenerating && (
                  <button
                    onClick={handleCancelGeneration}
                    className="w-full glassmorphic-button rounded-xl py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors"
                  >
                    <Icon name="X" size={16} />
                    Cancel
                  </button>
                )}

                <p className="text-white/50 text-xs text-center">Your request will cost {getModelCreditCost(selectedModel)} credit{getModelCreditCost(selectedModel) !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>
        </main>

        {/* Pinterest Modal Popup */}
        {isPinterestModalOpen &&
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="glassmorphic-card rounded-3xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative animate-fade-in">
              <button
              onClick={() => {
                setIsPinterestModalOpen(false);
                resetPinterestModal();
              }}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-10">

                <Icon name="X" size={20} />
              </button>
              
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Icon name="Link" size={32} className="text-orange-500" />
                  <h3 className="text-white text-2xl font-semibold">Pinterest Integration</h3>
                </div>
                
                {/* View Tabs */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setPinterestView('search')}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      pinterestView === 'search' ?'bg-orange-500 text-white' :'bg-white/5 text-white/70 hover:bg-white/10'
                    }`}>
                    Search Pins
                  </button>
                  <button
                    onClick={() => {
                      setPinterestView('boards');
                      if (pinterestBoards?.length === 0) {
                        handleLoadBoards();
                      }
                    }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      pinterestView === 'boards' ?'bg-orange-500 text-white' :'bg-white/5 text-white/70 hover:bg-white/10'
                    }`}>
                    My Boards
                  </button>
                </div>

                {/* Error Display */}
                {pinterestError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4">
                    <p className="text-red-400 text-sm">{pinterestError}</p>
                  </div>
                )}
              </div>

              {/* Search View */}
              {pinterestView === 'search' && (
                <div>
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      value={pinterestSearchQuery}
                      onChange={(e) => setPinterestSearchQuery(e?.target?.value)}
                      onKeyPress={(e) => e?.key === 'Enter' && handlePinterestSearch()}
                      placeholder="Search for interior design inspiration..."
                      className="flex-1 glassmorphic-input rounded-xl px-4 py-3 text-sm text-white bg-white/5 border border-white/10 focus:border-orange-500/50 focus:outline-none transition-all placeholder:text-white/30"
                    />
                    <button
                      onClick={handlePinterestSearch}
                      disabled={isPinterestLoading}
                      className="px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white rounded-xl font-medium transition-all flex items-center gap-2">
                      {isPinterestLoading ? (
                        <>
                          <Icon name="Loader2" size={18} className="animate-spin" />
                          Searching...
                        </>
                      ) : (
                        <>
                          <Icon name="Search" size={18} />
                          Search
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-white/50 text-xs mb-4">
                    Tip: Try searching "modern living room", "minimalist bedroom", or "scandinavian kitchen"
                  </p>
                </div>
              )}

              {/* Boards View */}
              {pinterestView === 'boards' && (
                <div>
                  {isPinterestLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Icon name="Loader2" size={32} className="text-orange-500 animate-spin" />
                    </div>
                  ) : pinterestBoards?.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {pinterestBoards?.map((board) => (
                        <button
                          key={board?.id}
                          onClick={() => handleBoardSelect(board)}
                          className="glassmorphic-card rounded-xl p-4 hover:scale-[1.02] transition-transform text-left">
                          {board?.imageUrl && (
                            <img
                              src={board?.imageUrl}
                              alt={board?.name}
                              className="w-full h-32 object-cover rounded-lg mb-3"
                            />
                          )}
                          <h4 className="text-white font-medium text-sm mb-1">{board?.name}</h4>
                          <p className="text-white/50 text-xs">{board?.pinCount} pins</p>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Icon name="Folder" size={48} className="text-white/30 mx-auto mb-4" />
                      <p className="text-white/60 text-sm">No boards found</p>
                    </div>
                  )}
                </div>
              )}

              {/* Pins Gallery View */}
              {pinterestView === 'pins' && (
                <div>
                  {selectedBoard && (
                    <button
                      onClick={() => {
                        setPinterestView('boards');
                        setSelectedBoard(null);
                        setPinterestPins([]);
                      }}
                      className="flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors">
                      <Icon name="ArrowLeft" size={16} />
                      <span className="text-sm">Back to Boards</span>
                    </button>
                  )}
                  
                  {isPinterestLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Icon name="Loader2" size={32} className="text-orange-500 animate-spin" />
                    </div>
                  ) : pinterestPins?.length > 0 ? (
                    <div>
                      <p className="text-white/70 text-sm mb-4">
                        Click on any image to add it to your style references
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-h-[500px] overflow-y-auto">
                        {pinterestPins?.map((pin) => (
                          <button
                            key={pin?.id}
                            onClick={() => handlePinSelect(pin)}
                            className="group relative rounded-xl overflow-hidden hover:scale-[1.02] transition-transform">
                            <img
                              src={pin?.imageUrl}
                              alt={pin?.alt}
                              className="w-full h-48 object-cover"
                            />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Icon name="Plus" size={32} className="text-white" />
                            </div>
                            {pin?.title && (
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                                <p className="text-white text-xs font-medium truncate">{pin?.title}</p>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Icon name="Image" size={48} className="text-white/30 mx-auto mb-4" />
                      <p className="text-white/60 text-sm">No pins found</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        }
      </div>
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)'
          }
        }}
      />

      {/* Virtual Tool Guide Overlay */}
      {showGuide && (
        <>
        <div
          className="fixed inset-0 z-[200]"
          onClick={closeGuide}
        >
          {/* Dim overlay */}
          <div
            className="absolute inset-0 transition-opacity duration-600 ease-[cubic-bezier(0.4,0,0.2,1)]"
            style={{ background: 'rgba(0, 0, 0, 0.30)', opacity: guideVisible ? 1 : 0 }}
          />
        </div>

        {/* Tooltip bubble - outside overlay for proper z-index */}
        <div
          className="fixed z-[9999] w-[280px] md:w-[300px] pointer-events-none"
            style={(() => {
              const desktopRefs = [style1UploadDesktopRef.current, style2UploadDesktopRef.current, roomTypeDesktopRef.current, furniturePresetsDesktopRef.current, modelDesktopRef.current, aspectRatioDesktopRef.current, resolutionGuideDesktopRef.current, promptDesktopRef.current, generateBtnDesktopRef.current, resultDesktopRef.current, assetLibraryDesktopRef.current];
              const mobileRefs = [style1UploadMobileRef.current, style2UploadMobileRef.current, roomTypeMobileRef.current, furniturePresetsMobileRef.current, modelMobileRef.current, aspectRatioMobileRef.current, resolutionGuideMobileRef.current, promptMobileRef.current, generateBtnMobileRef.current, resultMobileRef.current, assetLibraryMobileRef.current];
              const desktopRef = desktopRefs[guideStep - 1];
              const mobileRef = mobileRefs[guideStep - 1];
              const ref = window.innerWidth >= 768 ? desktopRef : mobileRef;
              const bubbleWidth = window.innerWidth >= 768 ? 300 : 280;
              if (!ref) return { top: '50%', left: '50%' };
              const rect = ref.getBoundingClientRect();
              if (guideStep === 10 || guideStep === 11) {
                return {
                  top: rect.top + rect.height / 2,
                  left: rect.left - bubbleWidth - 32,
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(24px)',
                  transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                };
              }
              return {
                top: rect.top + rect.height / 2,
                left: rect.right + 32,
                opacity: guideVisible ? 1 : 0,
                transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(-24px)',
                transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              };
            })()}
          >
            <div className="pointer-events-auto">
            {/* Arrow pointing left (default) or right (steps 10-11) */}
            {guideStep === 10 || guideStep === 11 ? (
              <>
                <div
                  className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '10px solid transparent',
                    borderBottom: '10px solid transparent',
                    borderLeft: '10px solid rgba(254, 131, 56, 0.3)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
                <div
                  className="absolute top-1/2 -right-[6px] -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '9px solid transparent',
                    borderBottom: '9px solid transparent',
                    borderLeft: '9px solid rgba(30, 30, 30, 0.95)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
              </>
            ) : (
              <>
                <div
                  className="absolute top-1/2 -left-2 -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '10px solid transparent',
                    borderBottom: '10px solid transparent',
                    borderRight: '10px solid rgba(254, 131, 56, 0.3)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
                <div
                  className="absolute top-1/2 -left-[6px] -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '9px solid transparent',
                    borderBottom: '9px solid transparent',
                    borderRight: '9px solid rgba(30, 30, 30, 0.95)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
              </>
            )}
            <div
              className="rounded-2xl p-5 text-center"
              style={{ background: 'rgb(24, 24, 24)', border: '1px solid rgba(254, 131, 56, 0.3)', boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(254, 131, 56, 0.1)' }}
            >
              <div
                className="flex items-center justify-center gap-2 mb-2"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s',
                }}
              >
                <Icon name={[null, "Upload", "Upload", "Home", "MousePointer2", "Cpu", "RectangleHorizontal", "SlidersHorizontal", "MessageSquare", "Sparkles", "Monitor", "FolderOpen"][guideStep] || "Settings"} size={18} className="text-white/50" />
                <span className="text-sm font-semibold text-white">Step {guideStep}</span>
              </div>
              <p
                className="text-sm text-white/80 leading-relaxed"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.2s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s',
                }}
              >
                {guideStep === 1 ? (
                  <>Upload reference images for <span className="text-white font-medium italic">Interior room style 1</span>. These images define the first design aesthetic that will be combined. Without a reference, the image generation will not start.</>
                ) : guideStep === 2 ? (
                  <>Upload reference images for <span className="text-white font-medium italic">Interior room style 2</span>. These images define the second design aesthetic. The AI will merge both styles into a single cohesive room.</>
                ) : guideStep === 3 ? (
                  <>Select the <span className="text-white font-medium italic">type of room</span> you want to generate. This determines the available furniture presets.</>
                ) : guideStep === 4 ? (
                  <>Drag and drop <span className="text-white font-medium italic">furniture presets</span> into the Style 1 or Style 2 zones below. Tagged furniture will carry the visual identity of that style in the final generation.</>
                ) : guideStep === 5 ? (
                  <>Image generating <span className="text-white font-medium italic">Models</span> which create images. Different models allow you to explore different results.</>
                ) : guideStep === 6 ? (
                  <>Image ratio: vertical or horizontal (choose the precise ratio according to the dropdown menu icons). Different <span className="text-white font-medium italic">ratios</span> allow you to explore different room compositions.</>
                ) : guideStep === 7 ? (
                  <>A dropdown selector that allows you to choose the <span className="text-white font-medium italic">output image resolution</span> for your generation. Higher resolution produces a more detailed and sharper image.</>
                ) : guideStep === 8 ? (
                  <>An optional <span className="text-white font-medium italic">text prompt</span> where you can add additional instructions or requests to refine the generated room design.</>
                ) : guideStep === 9 ? (
                  <>Hit <span className="text-white font-medium italic">'Generate Image'</span> once you're ready! Choose only the fields that are relevant to your needs.</>
                ) : guideStep === 10 ? (
                  <>Receive the generated image in the <span className="text-white font-medium italic">'Result'</span> window. Use the buttons in the bottom right corner to <span className="text-white font-medium italic">download</span>, <span className="text-white font-medium italic">delete</span>, or <span className="text-white font-medium italic">regenerate</span> the image using the exact same settings.</>
                ) : guideStep === 11 ? (
                  <>Generated images which you chose to store inside Roomwork.ai can be <span className="text-white font-medium italic">dragged and dropped</span> into 'Interior room style 1' or 'Interior room style 2'.</>
                ) : null}
              </p>
              {false && (
                <div
                  className="mt-3 flex justify-center"
                  style={{
                    opacity: guideVisible ? 1 : 0,
                    transform: guideVisible ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.95)',
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.25s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.25s',
                  }}
                >
                  <img
                    src="/assets/images/guide-moodboard-example.jpg"
                    alt="Style reference example"
                    className="w-28 h-28 object-cover rounded-xl"
                    style={{ border: '1px solid rgba(255, 255, 255, 0.1)' }}
                  />
                </div>
              )}
              <button
                onClick={() => guideStep < guideStepCount ? nextGuideStep() : closeGuide()}
                className="mt-4 px-5 py-2 glassmorphic-button rounded-xl text-white text-xs font-medium hover:scale-[1.02]"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.3s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s',
                }}
              >
                {guideStep < guideStepCount ? 'Next' : 'Got it'}
              </button>
            </div>
          </div>
        </div>
        </>
      )}
    </>
  );


};

export default CombineInteriorStyles;
